import { z } from 'zod'
import { connectorIdSchema } from './connections'
import { calendarDate } from './dates'

/**
 * The outbox holds everything hugo would send to another system. Entries start `pending` and
 * leave hugo only when the user approves them, through the writes gateway: `sent` when writes
 * are enabled, `dry_run` when they are not (recorded, never sent). `discarded`: the user threw
 * it away. `withdrawn`: hugo took back a heads-up or reminder that is no longer needed (the
 * person replied, the item was done or moved); `error` says why.
 */
export const outboxStatuses = [
  'pending',
  'sending',
  'sent',
  'dry_run',
  'failed',
  'discarded',
  'withdrawn',
] as const
export const outboxStatusSchema = z.enum(outboxStatuses)
export type OutboxStatus = z.output<typeof outboxStatusSchema>

/**
 * Where an entry can go from each status. A draft is approved (`sending`), refused on approval
 * (`failed`), discarded by the user or withdrawn by hugo; an approved entry is sent, recorded as
 * a dry run, or fails. Every other status is final.
 */
export const OUTBOX_TRANSITIONS: Readonly<Record<OutboxStatus, readonly OutboxStatus[]>> = {
  pending: ['sending', 'failed', 'discarded', 'withdrawn'],
  sending: ['sent', 'dry_run', 'failed'],
  sent: [],
  dry_run: [],
  failed: [],
  discarded: [],
  withdrawn: [],
}

export const canMove = (from: OutboxStatus, to: OutboxStatus): boolean =>
  OUTBOX_TRANSITIONS[from].includes(to)

/**
 * `reply`: a message in the item's thread. `set_status`: a change of the ask's state at its source
 * (a GitHub issue moved to Testing, closed or reopened; decision 108), with no recipients and no
 * message.
 */
export const outboxActions = ['reply', 'set_status'] as const
export const outboxActionSchema = z.enum(outboxActions)
export type OutboxAction = z.output<typeof outboxActionSchema>

/**
 * Why an entry was drafted: an answer in the thread, a heads-up about a date (M5a), a reminder to
 * someone the user waits on (M5b), or a status change the user asked for when marking an item done
 * (decision 108; sent without a second approval).
 */
export const outboxPurposes = ['reply', 'heads_up', 'reminder', 'status'] as const
export const outboxPurposeSchema = z.enum(outboxPurposes)
export type OutboxPurpose = z.output<typeof outboxPurposeSchema>

/** The purposes hugo drafted the text of: it withdraws those when they are no longer needed. */
export const HUGO_DRAFTED_PURPOSES: readonly OutboxPurpose[] = ['heads_up', 'reminder']

/** Why hugo withdrew an entry ("Marco replied", "Done"), at most this long. */
export const WITHDRAW_REASON_MAX = 200

export const REPLY_BODY_MAX = 10_000

/** The thread an entry answers in, for display ("Re: …" and a link to it). */
export const outboxThreadSchema = z.object({ subject: z.string(), url: z.string().nullable() })
export type OutboxThread = z.output<typeof outboxThreadSchema>

export const outboxEntrySchema = z.object({
  id: z.uuid(),
  itemId: z.uuid().nullable(),
  connector: connectorIdSchema,
  action: outboxActionSchema,
  status: outboxStatusSchema,
  summary: z.string(),
  recipients: z.array(z.string()),
  thread: outboxThreadSchema.nullable(),
  body: z.string().max(REPLY_BODY_MAX),
  createdAt: z.iso.datetime(),
  decidedAt: z.iso.datetime().nullable(),
  sentAt: z.iso.datetime().nullable(),
  /** Why it failed (`failed`), or why hugo withdrew it (`withdrawn`). */
  error: z.string().nullable(),
  purpose: outboxPurposeSchema.default('reply'),
  /**
   * The new date a heads-up about a late promise gives; approving the entry moves the item's due
   * date there. Null for every other entry.
   */
  promiseDate: calendarDate.nullable().default(null),
})
export type OutboxEntry = z.output<typeof outboxEntrySchema>
